/**
 * The alchemist (ADR 0026): in every village, he brews health potions out of
 * what the biome's creatures leave behind, and a little gold. Each village
 * brews its own biome's two: a light one from its first creature alone, a
 * strong one from both. The further from the capital, the more they heal.
 *
 * Validated with the other items in `items.ts`; `rules/alchemy.ts` checks
 * that every ingredient exists.
 */
/** The monster who keeps the alchemist's house (`data/mobs.ts`). */
export const ALCHEMIST_MOB_ID = 'alchemist';
/** How close to an alchemist a player must stand, in tiles, diagonals included. */
export const ALCHEMIST_REACH = 2;
/** How many potions one request may brew: a stack's worth at most. */
export const MAX_BREW_COUNT = 20;
/**
 * Heals sized on the biome's band: a Warrior has 120 health at level 1 and
 * about 300 by level 20, and the merchant's potions give 30 and 80. A light
 * potion gives a third of a fighter's health or so, a strong one a half.
 */
export const ALCHEMY = {
    meadow: {
        light: {
            id: 'meadow-tonic',
            name: 'Tonique des champs',
            heal: 40,
            levelRequired: 1,
            sellPrice: 2,
        },
        strong: {
            id: 'meadow-potion',
            name: 'Potion des champs',
            heal: 60,
            levelRequired: 1,
            sellPrice: 4,
        },
        recipes: {
            light: [{ templateId: 'rat-tail', quantity: 3 }],
            strong: [
                { templateId: 'rat-tail', quantity: 2 },
                { templateId: 'boar-tusk', quantity: 2 },
            ],
        },
        gold: { light: 1, strong: 2 },
    },
    forest: {
        light: {
            id: 'forest-tonic',
            name: 'Tonique de sylve',
            heal: 70,
            levelRequired: 5,
            sellPrice: 4,
        },
        strong: {
            id: 'forest-potion',
            name: 'Potion de sylve',
            heal: 100,
            levelRequired: 5,
            sellPrice: 7,
        },
        recipes: {
            light: [{ templateId: 'wolf-pelt', quantity: 2 }],
            strong: [
                { templateId: 'wolf-pelt', quantity: 2 },
                { templateId: 'goblin-ear', quantity: 2 },
            ],
        },
        gold: { light: 3, strong: 5 },
    },
    swamp: {
        light: {
            id: 'swamp-tonic',
            name: 'Tonique des marais',
            heal: 100,
            levelRequired: 8,
            sellPrice: 5,
        },
        strong: {
            id: 'swamp-potion',
            name: 'Potion des marais',
            heal: 140,
            levelRequired: 8,
            sellPrice: 11,
        },
        recipes: {
            light: [{ templateId: 'toad-slime', quantity: 2 }],
            strong: [
                { templateId: 'toad-slime', quantity: 2 },
                { templateId: 'lizard-scale', quantity: 2 },
            ],
        },
        gold: { light: 5, strong: 8 },
    },
    desert: {
        light: {
            id: 'desert-tonic',
            name: 'Tonique des sables',
            heal: 130,
            levelRequired: 12,
            sellPrice: 7,
        },
        strong: {
            id: 'desert-potion',
            name: 'Potion des sables',
            heal: 180,
            levelRequired: 12,
            sellPrice: 15,
        },
        recipes: {
            light: [{ templateId: 'hyena-fang', quantity: 2 }],
            strong: [
                { templateId: 'hyena-fang', quantity: 2 },
                { templateId: 'mummy-wrap', quantity: 2 },
            ],
        },
        gold: { light: 8, strong: 12 },
    },
    snow: {
        light: {
            id: 'snow-tonic',
            name: 'Tonique de givre',
            heal: 170,
            levelRequired: 16,
            sellPrice: 10,
        },
        strong: {
            id: 'snow-potion',
            name: 'Potion de givre',
            heal: 240,
            levelRequired: 16,
            sellPrice: 22,
        },
        recipes: {
            light: [{ templateId: 'bear-fur', quantity: 2 }],
            strong: [
                { templateId: 'bear-fur', quantity: 2 },
                { templateId: 'yeti-horn', quantity: 2 },
            ],
        },
        gold: { light: 12, strong: 18 },
    },
};
/** The potions as item templates, validated with the rest in `items.ts`. */
export const ALCHEMY_POTION_TEMPLATES = Object.entries(ALCHEMY).flatMap(([biome, alchemy]) => [alchemy.light, alchemy.strong].map((potion, strong) => ({
    id: potion.id,
    name: potion.name,
    kind: 'consumable',
    rarity: strong === 1 ? 'rare' : 'common',
    levelRequired: potion.levelRequired,
    maxStack: 20,
    heal: potion.heal,
    // One flask per biome, in its colours; light and strong tell apart by
    // the rim of their slot, common and rare.
    icon: `icon.potion-${biome}`,
    sellPrice: potion.sellPrice,
    // Brewed, never found: the alchemist is where they come from.
    lootable: false,
})));
//# sourceMappingURL=alchemy.js.map